/**
 * Lista de Projetos - Grid de cards ou Tabela
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Ship, Loader2, Calendar, DollarSign, Users, Edit, Trash2, ChevronRight } from 'lucide-react';
import CardProjeto from './CardProjeto';
import { STATUS_CONFIG, formatMoney, formatDate } from './constantes';

const ListaProjetos = ({ 
  projetos, 
  loading, 
  onEdit, 
  onDelete,
  viewMode = 'grid'
}) => {
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (projetos.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center h-64 text-gray-500">
          <Ship className="h-12 w-12 mb-4 opacity-30" />
          <p>Nenhum projeto encontrado</p>
          <p className="text-sm text-gray-400 mt-1">
            Crie um novo projeto ou ajuste os filtros
          </p>
        </CardContent>
      </Card>
    );
  }

  if (viewMode === 'list') {
    return (
      <Card data-testid="projetos-list-view">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-gray-50/80">
                <th className="text-left p-3 font-medium text-gray-600">Título</th>
                <th className="text-left p-3 font-medium text-gray-600">Cliente</th>
                <th className="text-left p-3 font-medium text-gray-600">Valor</th>
                <th className="text-left p-3 font-medium text-gray-600">Responsável</th>
                <th className="text-left p-3 font-medium text-gray-600">Embarcações</th>
                <th className="text-left p-3 font-medium text-gray-600">Entrega</th>
                <th className="text-left p-3 font-medium text-gray-600 min-w-[140px]">Progresso</th>
                <th className="text-left p-3 font-medium text-gray-600">Status</th>
                <th className="text-right p-3 font-medium text-gray-600">Ações</th>
              </tr>
            </thead>
            <tbody>
              {projetos.map((project) => {
                const statusConfig = STATUS_CONFIG[project.status] || STATUS_CONFIG['Planning'];
                const totalEmb = project.embarcacoes?.length || 0;
                const progress = Math.round(project.progress || 0);
                
                return (
                  <tr 
                    key={project.id} 
                    className="border-b hover:bg-blue-50/40 transition-colors cursor-pointer group"
                    data-testid={`projeto-row-${project.id}`}
                    onClick={() => {
                      if (totalEmb === 1) navigate(`/projects/${project.id}/embarcacao/0`);
                      else onEdit(project);
                    }}
                  >
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <Ship className="h-4 w-4 text-blue-600 flex-shrink-0" />
                        <span className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
                          {project.name}
                        </span>
                      </div>
                    </td>
                    <td className="p-3 text-gray-600">{project.client || '—'}</td>
                    <td className="p-3 text-gray-700 font-medium">{formatMoney(project.valor_venda ?? project.budget)}</td>
                    <td className="p-3 text-gray-600">{project.responsavel || '—'}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-1">
                        {totalEmb > 0 ? (
                          project.embarcacoes.slice(0, 4).map((emb, idx) => (
                            <Button
                              key={idx}
                              size="sm"
                              variant="outline"
                              className="h-6 px-2 text-xs"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/projects/${project.id}/embarcacao/${idx}`);
                              }}
                              title={`Embarcação #${emb.numero || idx + 1} — Ficha Técnica`}
                            >
                              #{emb.numero || idx + 1}
                            </Button>
                          ))
                        ) : (
                          <span className="text-gray-400 text-xs">Nenhuma</span>
                        )}
                        {totalEmb > 4 && (
                          <span className="text-xs text-gray-400">+{totalEmb - 4}</span>
                        )}
                      </div>
                    </td>
                    <td className="p-3 text-gray-600 whitespace-nowrap">{formatDate(project.delivery_date)}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-blue-500 rounded-full transition-all"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                        <span className="text-xs font-medium text-gray-600 w-8 text-right">{progress}%</span>
                      </div>
                    </td>
                    <td className="p-3">
                      <Badge className={`${statusConfig.color} text-xs`}>
                        {statusConfig.label}
                      </Badge>
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                          size="sm" variant="ghost" className="h-7 w-7 p-0"
                          onClick={(e) => { e.stopPropagation(); onEdit(project); }}
                          title="Editar"
                        >
                          <Edit className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                          onClick={(e) => { e.stopPropagation(); onDelete(project.id); }}
                          title="Excluir"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="projetos-grid-view">
      {projetos.map((project) => (
        <CardProjeto
          key={project.id}
          project={project}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ))}
    </div>
  );
};

export default ListaProjetos;
